import { create } from 'zustand';

import type {
  CardCombination,
  CardInterpretation,
  DrawResponse,
  DrawnCard,
  EntropyAudit,
  RitualMode,
} from '@lilith-arcana-os/domain';
import type { SpreadDefinition } from '@lilith-arcana-os/spread-registry';

import { bootstrapSpreadDefinitions } from '../lib/constants';

interface ReadingState {
  question: string;
  selectedSpread: SpreadDefinition;
  drawnCards: DrawnCard[];
  interpretations: CardInterpretation[];
  combinations: CardCombination[];
  entropyAudit: EntropyAudit | undefined;
  llmResponse: string | null | undefined;
  symbolicSummary: string;
  synthesis: string;
  isDrawing: boolean;
  isInterpreting: boolean;
  mode: RitualMode;
  setQuestion: (question: string) => void;
  setSelectedSpread: (spread: SpreadDefinition) => void;
  setMode: (mode: RitualMode) => void;
  startDrawing: () => void;
  finishDrawing: (response: DrawResponse) => void;
  startInterpreting: () => void;
  finishInterpreting: (payload: {
    interpretations: CardInterpretation[];
    combinations: CardCombination[];
    symbolicSummary: string;
    synthesis: string;
    llmResponse: string | null | undefined;
  }) => void;
  reset: () => void;
}

const initialSpread: SpreadDefinition = bootstrapSpreadDefinitions[0]!;

export const useReadingStore = create<ReadingState>((set) => ({
  question: '',
  selectedSpread: initialSpread,
  drawnCards: [],
  interpretations: [],
  combinations: [],
  entropyAudit: undefined,
  llmResponse: undefined,
  symbolicSummary: '',
  synthesis: '',
  isDrawing: false,
  isInterpreting: false,
  mode: 'standard',
  setQuestion: (question) => set({ question }),
  setSelectedSpread: (selectedSpread) => set({ selectedSpread }),
  setMode: (mode) => set({ mode }),
  startDrawing: () =>
    set({
      isDrawing: true,
      drawnCards: [],
      interpretations: [],
      combinations: [],
      entropyAudit: undefined,
      llmResponse: undefined,
      symbolicSummary: '',
      synthesis: '',
    }),
  finishDrawing: (response) =>
    set({
      isDrawing: false,
      drawnCards: response.cards,
      entropyAudit: response.audit,
    }),
  startInterpreting: () => set({ isInterpreting: true }),
  finishInterpreting: ({ combinations, interpretations, llmResponse, symbolicSummary, synthesis }) =>
    set({
      isInterpreting: false,
      combinations,
      interpretations,
      llmResponse,
      symbolicSummary,
      synthesis,
    }),
  reset: () =>
    set({
      question: '',
      selectedSpread: initialSpread,
      drawnCards: [],
      interpretations: [],
      combinations: [],
      entropyAudit: undefined,
      llmResponse: undefined,
      symbolicSummary: '',
      synthesis: '',
      isDrawing: false,
      isInterpreting: false,
      mode: 'standard',
    }),
}));
